'use-client';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import MeshContainer from '@wix/thunderbolt-elements/components/MeshContainer';
import ResponsiveContainer from '@wix/thunderbolt-elements/components/ResponsiveContainer';
import * as React from 'react';
import semanticClassNames from '../SelectableContainer.semanticClassNames';
import type {
  ISelectableContainerCommonProps,
  ISelectableContainerImperativeActions,
} from '../types';
import classNames from 'clsx';
import { SelectableContainerContextProvider } from '../SelectableContainer.context';
import { InputContent } from '../constants';
import { useFocusableHandle } from './hooks/useFocusableHandle';
import { useSelectVariant } from './hooks/useSelectVariant';

const SelectableContainerCommon: React.ForwardRefRenderFunction<
  ISelectableContainerImperativeActions,
  ISelectableContainerCommonProps
> = (props, ref) => {
  const {
    id,
    classes,
    children,
    className = '',
    customClassNames = [],
    containerRootClassName,
    containerProps,
    meshProps,
    hasResponsiveLayout,
    onSelect,
    selected,
    inputType = 'radio',
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    applySelectedVariant,
    removeSelectedVariant,
    isEditor,
    isInputVisible,
  } = props;

  const childrenToRender =
    typeof children === 'function' ? children : () => children;

  const inputRef = useFocusableHandle(ref);

  const handleSelect = (event: React.MouseEvent<HTMLElement>) => {
    onSelect(event);
    inputRef.current?.focus();
  };

  const inputAriaLabel =
    props.inputContent === InputContent.Button || !isInputVisible
      ? props.inputLabel
      : undefined;

  useSelectVariant({
    selected,
    applySelectedVariant,
    removeSelectedVariant,
    isEditor,
    id,
  });

  return (
    <SelectableContainerContextProvider
      inputName={props.radioButtonName}
      selected={selected}
      inputType={inputType}
      inputLabel={props.inputLabel}
      inputAriaLabel={inputAriaLabel}
      inputRef={inputRef}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <div
        id={id}
        className={classNames(
          containerRootClassName,
          className,
          classes.wrapper,
        )}
        {...getDataAttributes(props)}
        onClick={handleSelect}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div
          className={classNames(
            classes.root,
            { selected },
            formatClassNames(semanticClassNames.root, ...customClassNames),
          )}
        />
        {hasResponsiveLayout ? (
          <ResponsiveContainer
            containerLayoutClassName={`${id}-container`}
            overlowWrapperClassName={`${id}-overflow-container`}
            hasOverflow={false}
            {...containerProps}
          >
            {children}
          </ResponsiveContainer>
        ) : (
          <MeshContainer {...meshProps} id={id}>
            {childrenToRender}
          </MeshContainer>
        )}
      </div>
    </SelectableContainerContextProvider>
  );
};

export default React.forwardRef(SelectableContainerCommon);
